import { Text, Flex, Badge, Box, Anchor, Stack } from '@mantine/core';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetWorklistRecord } from 'hooks/api/worklist/useGetWorklistRecord';
import { formatDate } from 'utils/dates';
import { useMeasurementReportPdf } from 'hooks/api/useReports';
import { CenteredLoading } from 'components/CenteredLoading';
import { DocumentIdProps } from 'models/general';
import { containsAttachmentTypes, getPriorityInfo } from 'utils/worklist';
import { useNavigate } from 'react-router-dom';
import { PatientDetailsTabs } from 'pages/Main/Patients/Patients.helpers';
import { useDrawerContext } from 'context/hooks';
import { WorklistAttachmentType } from 'models/worklist';
import { useGetWorklistReportPdf } from 'hooks/api/worklist/useGetWorklistReportPdf';

export const WorklistDrawerDetails = ({ documentId }: DocumentIdProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { closeDrawer } = useDrawerContext();
  const { data: document } = useGetWorklistRecord({ documentId });

  const measurementId =
    containsAttachmentTypes({
      item: document,
      types: [WorklistAttachmentType.HeartRate, WorklistAttachmentType.HeartRhythmMeasurement],
    }) && document?.attachments?.measurementId
      ? document.attachments.measurementId
      : undefined;

  const { data: report, isFetching: isFetchingReport } = useGetWorklistReportPdf(document);
  const { data: measurement, isFetching: isFetchingMeasurement } = useMeasurementReportPdf({ measurementId });

  const isFetchingDocuments = isFetchingReport || isFetchingMeasurement;
  const hasReportDocument = !isFetchingDocuments && report;
  const hasMeasurementDocument = !isFetchingDocuments && !hasReportDocument && measurement;

  // Revoke the blob URLs only once they're no longer in use (on unmount or when replaced by a new one).
  // Revoking on the iframe's `load` event instead breaks the PDF viewer's built-in download button in
  // Chrome, which re-reads the blob URL whenever it's clicked, which can happen well after `load` fires.
  useEffect(() => {
    return () => {
      if (report) URL.revokeObjectURL(report);
    };
  }, [report]);

  useEffect(() => {
    return () => {
      if (measurement) URL.revokeObjectURL(measurement);
    };
  }, [measurement]);

  if (!document) return <CenteredLoading mt={'sm'} />;

  return (
    <>
      <Stack gap={'xs'}>
        <Flex justify={'flex-end'} mt={'xs'}>
          <Text c={'var(--mantine-color-dimmed)'}>
            {document?.lastInteraction?.timestamp &&
              t('status_updated_label', {
                date: `${formatDate({ timestamp: new Date(document.lastInteraction.timestamp), formatWithTime: true })}`,
              })}
            {document?.lastInteraction?.userName &&
              ` ${t('username_updated_label', { username: document?.lastInteraction.userName })}`}
          </Text>
        </Flex>
        <Badge color={getPriorityInfo(document?.content.priority).color} size={'lg'} radius={'sm'} variant={'filled'}>
          {document?.content.title}
        </Badge>
        <Text size={'sm'} fs={'italic'}>
          {formatDate({
            timestamp: document?.notificationCreationTimestamp || 0,
            formatWithTime: true,
            timeWithSeconds: false,
          })}
        </Text>
        <Anchor
          fw={'bold'}
          size={'sm'}
          c={'var(--mantine-color-turquoise-7)'}
          onClick={() => {
            closeDrawer();
            navigate(`/patients/${document?.attachments?.patientId}/details/${PatientDetailsTabs.Rules}`);
          }}
          mb={'sm'}
        >
          {`${document?.attachments.patientLastName} ${document?.attachments.patientFirstName}`}
        </Anchor>
      </Stack>
      {isFetchingDocuments && <CenteredLoading />}
      {hasReportDocument && (
        <Box h={'calc(100vh - 275px)'}>
          <iframe src={report} width={'100%'} height={'100%'} />
        </Box>
      )}
      {hasMeasurementDocument && (
        <Box h={'calc(100vh - 275px)'}>
          <iframe src={measurement} width={'100%'} height={'100%'} />
        </Box>
      )}
    </>
  );
};
